package com.evoapps.evoshift.domain.usecase.kiosk

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.KioskClockEvent
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskException
import com.evoapps.evoshift.domain.model.KioskOfflineException
import com.evoapps.evoshift.domain.repository.KioskRepository
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.random.Random
import kotlin.time.Instant

/** What a sync of the offline queue did: [sent] accepted, [refused] by the server, [left] waiting. */
data class KioskSyncReport(val sent: Int, val refused: Int, val left: Int)

/**
 * Clocks a person in or out on the tablet: online through the kioskClock function, which checks
 * the PIN and applies the same clock rules as the phone (ClockInClockOutUseCase / ClockRules) and
 * writes the same actual times; offline the event waits in [queue] with the device time and is
 * sent by [sync] once the tablet is back online (the server then marks it syncedLate).
 */
class KioskClockUseCase(
    private val repository: KioskRepository,
    private val queue: KioskClockQueue,
    private val now: () -> Instant = { AppClock.now() },
    private val newEventId: () -> String = { randomEventId() }
) {
    /**
     * Clocks [userId] with [pin]. [expected]: the action the tablet expects from the shifts it
     * has; a queued event holds the server to it. [shiftId] when the person picked a shift.
     */
    suspend fun clock(
        userId: String,
        pin: String,
        displayName: String,
        timeZone: TimeZone,
        shiftId: String? = null,
        expected: ClockAction? = null,
        /** SH-116: the person chose this (start a break, or clock out during one); null: the main button. */
        action: ClockAction? = null
    ): Result<KioskClockResult> {
        val tapped = now()
        val event = KioskClockEvent(
            eventId = newEventId(),
            userId = userId,
            shiftId = shiftId,
            // Online the server decides from the shift as it is now (it may have changed), unless
            // the person chose what to do.
            action = action,
            pin = pin,
            deviceTime = tapped.toString()
        )
        return repository.clock(event).recoverCatching { e ->
            if (e !is KioskOfflineException) throw e
            // Offline: what the tablet showed, so a second tap does not turn into the opposite.
            val offlineAction = action ?: expected
            queue.add(event.copy(action = offlineAction, queued = true))
            KioskClockResult(offlineAction, tapped.hhmm(timeZone), displayName, queued = true)
        }
    }

    /**
     * Sends the queued events, oldest first. One the server refuses (wrong PIN, nothing to clock)
     * is dropped and counted; the first one that cannot reach the server stops the sync.
     */
    suspend fun sync(): KioskSyncReport {
        var sent = 0
        var refused = 0
        for (event in queue.events.value) {
            val result = repository.clock(event)
            val error = result.exceptionOrNull()
            when {
                error == null -> sent++
                error is KioskOfflineException -> break
                // A tablet that was unpaired keeps nothing (it is signed out next).
                error is KioskException && error.reason == KioskException.Reason.REVOKED -> {
                    queue.clear()
                    return KioskSyncReport(sent, refused + 1, 0)
                }
                else -> refused++
            }
            queue.remove(event.eventId)
        }
        return KioskSyncReport(sent, refused, queue.events.value.size)
    }

    private fun Instant.hhmm(zone: TimeZone): String = toLocalDateTime(zone).let {
        "${it.hour.toString().padStart(2, '0')}:${it.minute.toString().padStart(2, '0')}"
    }
}

private fun randomEventId(): String {
    val alphabet = "abcdefghijklmnopqrstuvwxyz0123456789"
    return (1..24).map { alphabet[Random.nextInt(alphabet.length)] }.joinToString("")
}
